import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import './styles/globals.css';
import './styles/components.scss';
import './styles/newstore.scss';
import { motion, AnimatePresence } from 'framer-motion';
import { TopBar } from './components/TopBar';
import { OrderFeed } from './components/OrderFeed';
import { PullToRefreshIndicator } from './components/PullToRefreshIndicator';
import { usePullToRefresh } from './hooks/usePullToRefresh';
import { BottomNav } from './components/BottomNav';
import { CoinPackagesModal } from './components/CoinPackagesModal';
import { StickySectionHeader } from './components/StickySectionHeader';
import { SectionType } from './components/SectionHeader';
import { OrderDetailsModal } from './components/OrderDetailsModal';
import { FilterBottomSheet } from './components/FilterBottomSheet';
import { BuyCoinsModal } from './components/BuyCoinsModal';
import { IdentityValidationModal } from './components/IdentityValidationModal';
import { PrototypeSettingsModal } from './components/PrototypeSettingsModal';
import { PrototypeSettingsScreen } from './components/PrototypeSettingsScreen';
import { AccountTab } from './components/AccountTab';
import { OnboardingStep } from './components/OnboardingStepper';
import { Order } from './types/order';
import { TipCardConfig, defaultTipCardConfigs } from './types/tipcard';
import { useDismissedTips } from './hooks/useDismissedTips';
import { axiosInstance } from '@/utils/axiosInstance';
import { IUser } from '@/types/user';
import { OffersService } from '@/services/store/offers';
import { IOffer, ISort } from '@/services/store/interfaces/offers.interfaces';
import { redirect, useRouter } from 'next/navigation';
import { Toast, showToast } from '@/components/atoms/Toast/Toast';
import { UserService } from '@/services/store/user';
import {
  CmsTooltipsService,
  CmsTooltipSection,
  ICmsTooltip,
} from '@/services/store/cmsTooltips';

export type FilterState = {
  urgente: boolean;
  naoUrgente: boolean;
  presencial: boolean;
  remoto: boolean;
  ninguemLiberou: boolean;
  outrosLiberaram: boolean;
  naoAvaliados: boolean;
  gostei: boolean;
  naoGostei: boolean;
};

export type OrderAction =
  | 'like'
  | 'dislike'
  | 'dislike-removed'
  | 'report'
  | null;

/**
 * Cor de fundo de cada seção do feed, usada para colorir o indicador de
 * pull-to-refresh de acordo com a primeira seção preenchida.
 */
const SECTION_COLORS: Record<SectionType, string> = {
  express: '#F3EBFF',
  'para-voce': '#D1FAE7',
  outros: '#F0F2F5',
};

interface INewStoreWebTemplate {
  authToken: string;
  origin: string;
  shouldValidateDocument: boolean;
  validationUrl: string;
  profileId: number;
  initialOfferId?: string | null;
  likedRequestIds?: string[];
}

export default function NewStoreWebTemplate({
  authToken,
  origin,
  shouldValidateDocument,
  validationUrl,
  profileId,
  initialOfferId = null,
  likedRequestIds = [],
}: INewStoreWebTemplate) {
  const router = useRouter();

  const [userInfo, setUserInfo] = useState<IUser | null>(null);
  const [activeTab, setActiveTab] = useState('store');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

  // Reabre o pedido que estava aberto antes do redirecionamento para a loja
  // de moedas. Quando o usuário volta por aquele fluxo, o `offer_id` vem na
  // URL e reabrimos os detalhes (o modal busca o restante dos dados pela API).
  useEffect(() => {
    if (!initialOfferId) return;

    setSelectedOrder({ id: initialOfferId, isViewed: true } as unknown as Order);
  }, [initialOfferId]);
  const [coinsBalance, setCoinsBalance] = useState(0);
  const [viewMode, setViewMode] = useState<'cards' | 'list'>('list');
  // Tips dispensados persistidos em localStorage: ao clicar em "Entendi" o tip
  // some e não volta após recarregar a tela. Vale para todos os tip cards.
  const { dismissedTips, tipCardState, dismissTip, resetTip } =
    useDismissedTips();
  const [orderActions, setOrderActions] = useState<Record<string, OrderAction>>(
    {}
  );
  const [onboardingStep, setOnboardingStep] = useState<OnboardingStep>(4);
  const [filterSheet, setFilterSheet] = useState<{
    isOpen: boolean;
    type: 'location' | 'sort' | 'filters' | 'categories' | null;
  }>({
    isOpen: false,
    type: null,
  });
  const [showBuyCoinsModal, setShowBuyCoinsModal] = useState(false);
  const [showIdentityValidationModal, setShowIdentityValidationModal] =
    useState(false);
  const [showCoinPackagesModal, setShowCoinPackagesModal] = useState(false);
  const [topBarHeight, setTopBarHeight] = useState(60);
  const [currentSection, setCurrentSection] = useState<SectionType | null>(
    null
  );
  const [firstSection, setFirstSection] = useState<SectionType | null>(null);
  const [isMenuExpanded, setIsMenuExpanded] = useState(false);
  const [showPrototypeSettings, setShowPrototypeSettings] = useState(false);
  const [showPrototypeSettingsScreen, setShowPrototypeSettingsScreen] =
    useState(false);

  const [offerList, setOfferList] = useState<IOffer[]>([]);
  const [boughtOrderIds, setBoughtOrderIds] = useState<string[]>([]);
  const [removedOrderIds, setRemovedOrderIds] = useState<string[]>([]);
  const [isLoadingSort, setIsLoadingSort] = useState(false);
  const [reloadTrigger, setReloadTrigger] = useState(0);

  const [sortOption, setSortOption] = useState('recommended');
  const [filters, setFilters] = useState<FilterState>({
    urgente: true,
    naoUrgente: true,
    presencial: true,
    remoto: true,
    ninguemLiberou: true,
    outrosLiberaram: true,
    naoAvaliados: true,
    gostei: true,
    naoGostei: true,
  });
  const [selectedCategories, setSelectedCategories] = useState<string[]>([
    'Eletricista',
    'Encanador',
    'Assistência Técnica para Celular',
  ]);

  const [sortOptions, setSortOptions] = useState<ISort[]>([]);

  const [tipCardConfigs, setTipCardConfigs] = useState<TipCardConfig[]>(
    defaultTipCardConfigs
  );

  const [radius, setRadius] = useState<number>();

  /**
   * CMS-driven tooltips (tip cards) grouped por seção (Express, Para você, Outros).
   * Cada seção recebe uma lista ordenada de tooltips retornados pela API.
   * Se a API não retornar nada para uma seção, aquela seção simplesmente não terá tooltips.
   */
  const [cmsTooltipsBySection, setCmsTooltipsBySection] = useState<
    Partial<Record<CmsTooltipSection, ICmsTooltip[]>>
  >({});
  const [isLoadingCmsTooltips, setIsLoadingCmsTooltips] = useState(false);
  const [cmsTooltipsError, setCmsTooltipsError] = useState<Error | null>(null);

  const handleOpenFilter = (
    type: 'location' | 'sort' | 'filters' | 'categories'
  ) => {
    setFilterSheet({ isOpen: true, type });
  };

  const handleCloseFilter = () => {
    setFilterSheet({ isOpen: false, type: null });
  };

  const handlePurchaseCoins = (coinsAdded: number) => {
    setCoinsBalance((prev) => prev + coinsAdded);
  };

  /**
   * Remove o pedido recém-comprado do feed em tempo de execução, sem disparar
   * um reload da listagem. Assim o profissional não perde a posição de rolagem.
   * No próximo reload orgânico o backend já não devolve o pedido comprado.
   */
  const handleOfferBought = useCallback((orderId: string) => {
    setBoughtOrderIds((prev) =>
      prev.includes(orderId) ? prev : [...prev, orderId]
    );
  }, []);

  // Remove o pedido rejeitado da listagem atual (sem nova chamada à API).
  const handleOfferRemoved = useCallback((orderId: string) => {
    setRemovedOrderIds((prev) =>
      prev.includes(orderId) ? prev : [...prev, orderId]
    );
  }, []);

  /**
   * Resolver pendente do pull-to-refresh. Guardamos a função `resolve` da
   * Promise devolvida ao gesto e a executamos quando o OrderFeed sinaliza
   * que terminou de recarregar (onReloadComplete), encerrando o spinner.
   */
  const reloadResolveRef = useRef<(() => void) | null>(null);

  const handlePullRefresh = useCallback(
    () =>
      new Promise<void>((resolve) => {
        reloadResolveRef.current = resolve;
        setReloadTrigger((prev) => prev + 1);
      }),
    []
  );

  const handleReloadComplete = useCallback(() => {
    reloadResolveRef.current?.();
    reloadResolveRef.current = null;
  }, []);

  // Mantém a última seção conhecida (não volta para null durante o reload,
  // quando a lista é esvaziada), evitando piscar a cor do indicador.
  const handleFirstSectionChange = useCallback(
    (section: SectionType | null) => {
      if (section) setFirstSection(section);
    },
    []
  );

  const userInfoCall = useCallback(async () => {
    await axiosInstance({
      headers: { Authorization: authToken },
      method: 'GET',
      url: '/seja-um-profissional/api/store/user/current',
    }).then(({ data: res }) => {
      setUserInfo(res);
      setCoinsBalance(res.profile.balance);

      /**
       * Define o passo inicial do onboarding com base no status de documentação.
       *
       * - Se `documentation_approved === false`, o profissional ainda não validou a conta,
       *   então mostramos o passo 1 do onboarding (validar conta / verificação facial).
       * - Se `documentation_approved === true`, consideramos o onboarding concluído (passo 4),
       *   e o stepper deixa de ser exibido.
       */
      // TODO: Fluxo de onboarding desativado temporariamente — ninguém deve cair nele.
      // Para reativar, descomente o bloco abaixo e remova o setOnboardingStep(4) logo após.
      // const documentationApproved = res.profile.documentation_approved;
      // const initialStep: OnboardingStep = documentationApproved ? 4 : 1;
      // setOnboardingStep(initialStep);
      setOnboardingStep(4 as OnboardingStep);
    });
  }, [authToken]);

  const getFiltersAndSort = useCallback(async () => {
    await OffersService.getFiltersAndSort(authToken).then((res) =>
      setSortOptions(res.sort)
    );
  }, []);

  const handleGetUserLocation = useCallback(async (authToken: string) => {
    await UserService.getUserLocation(authToken).then((res) => {
      setRadius(res.radius);
    });
  }, []);

  /**
   * Busca os tooltips (tip cards) do CMS para o profile atual e
   * agrupa por seção já em ordem de exibição (is_first_in_section, priority, id).
   * Se não houver tooltips para uma seção, ela fica vazia (nenhum tooltip é exibido).
   */
  const fetchCmsTooltips = useCallback(async () => {
    // Só evita chamada se o profileId for realmente ausente (undefined/null).
    // Se for 0, ainda chamamos a API para permitir testes.
    if (profileId == null) return;

    try {
      setIsLoadingCmsTooltips(true);
      setCmsTooltipsError(null);

      const tooltips = await CmsTooltipsService.getTooltips(profileId);

      const grouped: Partial<Record<CmsTooltipSection, ICmsTooltip[]>> = {};

      (['express', 'para-voce', 'outros'] as CmsTooltipSection[]).forEach(
        (section) => {
          const forSection = tooltips.filter((t) => t.section === section);

          if (!forSection.length) return;

          // Ordena dentro da seção:
          // 1) is_first_in_section = true primeiro
          // 2) priority (maior primeiro)
          // 3) id (crescente)
          const sorted = [...forSection].sort((a, b) => {
            if (a.is_first_in_section !== b.is_first_in_section) {
              return a.is_first_in_section ? -1 : 1;
            }
            if (b.priority !== a.priority) {
              return b.priority - a.priority;
            }
            return a.id - b.id;
          });

          grouped[section] = sorted;
        }
      );

      setCmsTooltipsBySection(grouped);
    } catch (error) {
      setCmsTooltipsError(error as Error);
    } finally {
      setIsLoadingCmsTooltips(false);
    }
  }, [profileId]);

  useEffect(() => {
    userInfoCall();
    handleGetUserLocation(authToken);
    getFiltersAndSort();
    void fetchCmsTooltips();
  }, [authToken, fetchCmsTooltips]);

  const defaultFilters: FilterState = {
    urgente: true,
    naoUrgente: true,
    presencial: true,
    remoto: true,
    ninguemLiberou: true,
    outrosLiberaram: true,
    naoAvaliados: true,
    gostei: true,
    naoGostei: true,
  };

  const isFiltersDefault =
    filters.urgente === defaultFilters.urgente &&
    filters.naoUrgente === defaultFilters.naoUrgente &&
    filters.presencial === defaultFilters.presencial &&
    filters.remoto === defaultFilters.remoto &&
    filters.ninguemLiberou === defaultFilters.ninguemLiberou &&
    filters.outrosLiberaram === defaultFilters.outrosLiberaram &&
    filters.naoAvaliados === defaultFilters.naoAvaliados &&
    filters.gostei === defaultFilters.gostei &&
    filters.naoGostei === defaultFilters.naoGostei;

  const handleClearFilters = () => {
    setFilters(defaultFilters);
  };

  const handleDismissTip = (tipId: string) => {
    dismissTip(tipId);
  };

  const handleResetTip = (tipId: string) => {
    resetTip(tipId);
  };

  const handleSetOrderAction = (orderId: string, action: OrderAction) => {
    setOrderActions((prev) => ({
      ...prev,
      [orderId]: action,
    }));
  };

  // Conjunto de requestIds curtidos vindo do server. O `requestId` é o
  // segundo segmento do `order.id` (ex.: "offer-123" -> "123"), mesmo valor
  // usado no like/unlike.
  const likedRequestSet = useMemo(
    () => new Set(likedRequestIds),
    [likedRequestIds]
  );

  // Ao carregar pedidos, marca como "like" os que estão na lista de curtidas
  // do profissional. Só semeia quando ainda não há ação registrada para o
  // pedido, preservando toggles feitos pelo usuário na sessão (inclusive
  // "descurtir", que grava a chave como null).
  const handleOrdersLoaded = useCallback(
    (orderIds: string[]) => {
      if (likedRequestSet.size === 0) return;

      setOrderActions((prev) => {
        let changed = false;
        const next = { ...prev };

        orderIds.forEach((orderId) => {
          const requestId = orderId.split('-')[1];
          if (
            requestId &&
            likedRequestSet.has(requestId) &&
            !(orderId in next)
          ) {
            next[orderId] = 'like';
            changed = true;
          }
        });

        return changed ? next : prev;
      });
    },
    [likedRequestSet]
  );

  const handleOnboardingAction = (step: OnboardingStep) => {
    if (step === 0) {
      // Fluxo de reverificação concluída vai direto para "concluído"
      setTimeout(() => {
        setOnboardingStep(4 as OnboardingStep);
      }, 500);
    } else if (step === 1) {
      // Após concluir a etapa 1 (validar conta), só mostramos a etapa 2
      // ("Ver pacotes de moedas") se o saldo atual for 0.
      setTimeout(() => {
        if (coinsBalance === 0) {
          setOnboardingStep(2 as OnboardingStep);
        } else {
          // Se já tiver moedas, pulamos direto para a etapa 3.
          setOnboardingStep(3 as OnboardingStep);
        }
      }, 500);
    } else if (step === 2) {
      setTimeout(() => {
        setOnboardingStep(3 as OnboardingStep);
      }, 500);
    } else if (step === 3) {
      setTimeout(() => {
        setOnboardingStep(4 as OnboardingStep);
      }, 500);
    }
  };

  const handleSelectOrder = (order: Order) => {
    if (onboardingStep === 0) {
      setShowIdentityValidationModal(true);
    } else if (onboardingStep === 1) {
      setShowIdentityValidationModal(true);
    } else if (onboardingStep === 2) {
      setSelectedOrder(order);
    } else {
      setSelectedOrder(order);
    }
  };

  const handleClearSort = async () => {
    setIsLoadingSort(true);

    await OffersService.getOffers({ authToken, limit: 50 })
      .then((res) => {
        setOfferList(res.offers.offers_list);
        setIsLoadingSort(false);
        setSortOption('recommended');
        handleCloseFilter();
      })
      .catch((error) => console.error(error));
  };

  const handleApplySort = async () => {
    const filters = {
      only: [],
      categories_slugs: [],
      radius: radius || 70,
    };

    setIsLoadingSort(true);

    await OffersService.getOffers({
      authToken,
      sort: sortOption,
      filters,
    })
      .then((res) => {
        setOfferList(res.offers.offers_list);
        setIsLoadingSort(false);
        handleCloseFilter();
      })
      .catch(() => {
        setSortOption('recommended');
        setIsLoadingSort(false);
      });
  };

  const handleOpenStore = () => {
    router.push(`seja-/pagamentos/ios?auth_token=${authToken}`);
  };

  const ordersDisabled = onboardingStep === 0 || onboardingStep === 1;

  const activeFiltersCount = Object.keys(filters).filter((key) => {
    const filterKey = key as keyof FilterState;
    return filters[filterKey] !== defaultFilters[filterKey];
  }).length;

  // Pull-to-refresh do feed de pedidos. Desabilitado quando há sobreposições
  // ativas (onboarding bloqueado, modal de pedido, filtros, menu expandido).
  const isPullToRefreshDisabled =
    activeTab !== 'store' ||
    ordersDisabled ||
    !!selectedOrder ||
    filterSheet.isOpen ||
    isMenuExpanded;

  const { pullDistance, isRefreshing, isPulling } = usePullToRefresh({
    onRefresh: handlePullRefresh,
    disabled: isPullToRefreshDisabled,
  });

  return (
    <div
      className="min-h-screen flex items-center justify-center"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: '#1a1a1a',
        padding: '0',
      }}
    >
      <div
        className="min-h-screen flex flex-col w-full max-w-md relative"
        style={{
          borderRadius: 'min(24px, 2vw)',
          overflow: 'hidden',
          boxShadow:
            '0 20px 60px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.05)',
          padding: '0',
        }}
      >
        {activeTab === 'store' && (
          <>
            <TopBar
              onOpenFilter={handleOpenFilter}
              coinsBalance={coinsBalance}
              onOpenBuyCoins={handleOpenStore}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              sortOption={sortOption}
              isFiltersActive={!isFiltersDefault}
              isCategoriesActive={selectedCategories.length > 0}
              onboardingStep={onboardingStep}
              onOnboardingAction={handleOnboardingAction}
              ordersDisabled={ordersDisabled}
              activeFiltersCount={activeFiltersCount}
              selectedCategoriesCount={selectedCategories.length}
              isMenuExpanded={isMenuExpanded}
              onMenuExpandedChange={setIsMenuExpanded}
              onHeightChange={setTopBarHeight}
              userInfo={userInfo as IUser}
            />
          </>
        )}

        {activeTab === 'store' && (
          <AnimatePresence mode="wait">
            <StickySectionHeader
              currentSection={currentSection}
              topBarHeight={topBarHeight}
            />
          </AnimatePresence>
        )}

        <main className="flex-1 overflow-y-auto pb-24 relative">
          {activeTab === 'store' && (
            <>
              <PullToRefreshIndicator
                pullDistance={pullDistance}
                isRefreshing={isRefreshing}
                threshold={70}
                topBarHeight={topBarHeight}
                backgroundColor={
                  // Durante a puxada, usa a cor da seção exibida no topo.
                  // Quando o loading começa, o skeleton sempre mostra o Express
                  // primeiro, então a faixa acompanha a cor do Express.
                  isRefreshing
                    ? SECTION_COLORS.express
                    : firstSection
                      ? SECTION_COLORS[firstSection]
                      : undefined
                }
              />
              <div
                style={{
                  transform: `translateY(${pullDistance}px)`,
                  // Segue o dedo 1:1 enquanto arrasta; ao soltar, volta com
                  // um leve overshoot (efeito de bounce).
                  transition: isPulling
                    ? 'none'
                    : 'transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  willChange: 'transform',
                }}
              >
                <OrderFeed
                  userRadius={radius}
                  onSelectOrder={handleSelectOrder}
                  viewMode={viewMode}
                  sortOption={sortOption}
                  filters={filters}
                  onClearFilters={handleClearFilters}
                  dismissedTips={dismissedTips}
                  onDismissTip={handleDismissTip}
                  tipCardConfigs={tipCardConfigs}
                  tipCardState={tipCardState}
                  orderActions={orderActions}
                  ordersDisabled={ordersDisabled}
                  onCurrentSectionChange={setCurrentSection}
                  topBarHeight={topBarHeight}
                  authToken={authToken}
                  offerList={offerList}
                  profileId={profileId}
                  reloadTrigger={reloadTrigger}
                  boughtOrderIds={boughtOrderIds}
                  removedOrderIds={removedOrderIds}
                  onReloadComplete={handleReloadComplete}
                  onFirstSectionChange={handleFirstSectionChange}
                  cmsTooltipsBySection={cmsTooltipsBySection}
                  onOrdersLoaded={handleOrdersLoaded}
                />
              </div>
              <AnimatePresence>
                {isMenuExpanded && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{
                      duration: 0.3,
                      ease: [0.4, 0, 0.2, 1],
                    }}
                    className="absolute inset-0 pointer-events-auto"
                    style={{
                      backgroundColor: 'rgba(0, 0, 0, 0.3)',
                      backdropFilter: 'blur(4px)',
                      WebkitBackdropFilter: 'blur(4px)',
                      zIndex: 30,
                    }}
                    onClick={() => setIsMenuExpanded(false)}
                  />
                )}
              </AnimatePresence>
              {ordersDisabled && (
                <div
                  className="absolute pointer-events-auto"
                  style={{
                    top: `${topBarHeight}px`,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.12)',
                    transition: 'opacity 0.3s ease',
                    zIndex: 35,
                  }}
                  onClick={() => {
                    if (onboardingStep === 0 || onboardingStep === 1) {
                      setShowIdentityValidationModal(true);
                    }
                  }}
                />
              )}
            </>
          )}
        </main>

        {activeTab === 'store' && (
          <OrderDetailsModal
            order={selectedOrder}
            isOpen={!!selectedOrder}
            onClose={() => setSelectedOrder(null)}
            coinsBalance={coinsBalance}
            onPurchaseCoins={handlePurchaseCoins}
            orderAction={
              selectedOrder ? orderActions[selectedOrder.id] || null : null
            }
            onSetOrderAction={handleSetOrderAction}
            onboardingStep={onboardingStep}
            onOpenCoinPackages={() => setShowCoinPackagesModal(true)}
            onSelectOrder={setSelectedOrder}
            authToken={authToken}
            shouldValidateDocument={shouldValidateDocument}
            validationUrl={validationUrl}
            handleDisplayErrorNotFound={() => {}}
            onOfferBought={handleOfferBought}
            onOfferRemoved={handleOfferRemoved}
          />
        )}

        {activeTab === 'store' && (
          <FilterBottomSheet
            isOpen={filterSheet.isOpen}
            type={filterSheet.type}
            onClose={handleCloseFilter}
            sortOption={sortOption}
            onSortChange={setSortOption}
            filters={filters}
            onFiltersChange={setFilters}
            selectedCategories={selectedCategories}
            onCategoriesChange={setSelectedCategories}
            onOpenPrototypeSettings={() => setShowPrototypeSettings(true)}
            userInfo={userInfo as IUser}
            sortOptions={sortOptions}
            handleApplySort={handleApplySort}
            isLoadingSort={isLoadingSort}
            authToken={authToken}
            handleClearSort={handleClearSort}
            onLocationUpdated={userInfoCall}
          />
        )}

        {activeTab === 'store' && (
          <BuyCoinsModal
            isOpen={showBuyCoinsModal}
            onClose={() => setShowBuyCoinsModal(false)}
            onPurchaseComplete={handlePurchaseCoins}
            missingCoins={0}
            currentBalance={coinsBalance}
          />
        )}

        <IdentityValidationModal
          isOpen={showIdentityValidationModal}
          onClose={() => setShowIdentityValidationModal(false)}
          onStartVerification={() => {
            setShowIdentityValidationModal(false);
            if (onboardingStep === 0) {
              // Fluxo de reverificação: terminou, consideramos concluído.
              setOnboardingStep(4 as OnboardingStep);
            } else {
              // Fluxo normal: se ainda não tiver moedas, vai para etapa 2;
              // se já tiver saldo > 0, pula direto para etapa 3.
              if (coinsBalance === 0) {
                setOnboardingStep(2 as OnboardingStep);
              } else {
                setOnboardingStep(3 as OnboardingStep);
              }
            }
          }}
        />

        <CoinPackagesModal
          isOpen={showCoinPackagesModal}
          onClose={() => setShowCoinPackagesModal(false)}
          onPurchasePackage={() => {
            setShowCoinPackagesModal(false);
            setOnboardingStep(3 as OnboardingStep);
          }}
        />

        <PrototypeSettingsModal
          isOpen={showPrototypeSettings}
          onClose={() => setShowPrototypeSettings(false)}
          currentStep={onboardingStep}
          onStepChange={(step) => {
            setOnboardingStep(step);
            setActiveTab('store');
          }}
        />

        {showPrototypeSettingsScreen && (
          <PrototypeSettingsScreen
            isOpen={showPrototypeSettingsScreen}
            onClose={() => setShowPrototypeSettingsScreen(false)}
            currentStep={onboardingStep}
            onStepChange={setOnboardingStep}
            coinsBalance={coinsBalance}
            onCoinsBalanceChange={setCoinsBalance}
          />
        )}
      </div>

      <Toast />
    </div>
  );
}
